import { isUndefined } from 'ramda-adjunct'
import { computed, ComputedRef, Ref } from 'vue'

import { Answer, AnswerValue, assertAnswerType } from '../../answer/models/Answer'
import { isSpecialFieldId } from '../../question/models/Field'
import { Question } from '../../question/models/Question'
import { Prompt } from '../models/Prompt'
import { QUESTION_POLICIES } from '../stores/interview/QUESTION_POLICIES'
import { getDefaultAnswerValue } from '../utils/getDefaultAnswerValue'

type UseAnswerUtils = (
  answers: Ref<Answer[]>,
  invalidAnswerIds: Ref<string[]>,
  currentPromptType: ComputedRef<
    | { type: Exclude<Prompt['type'], 'question'> }
    | {
        type: 'question'
        question:
          | {
              fields: Question['fields']
              type: Exclude<Question['type'], 'choice' | 'imageChoice'>
            }
          | {
              fields: Question['fields']
              type: 'choice'
              maxAnswers: Question<'choice'>['maxAnswers']
            }
          | {
              fields: Question['fields']
              type: 'imageChoice'
              maxAnswers: Question<'imageChoice'>['maxAnswers']
            }
      }
    | undefined
  >,
) => {
  getAnswerByFieldId: (fieldId: string) => Answer
  isSpecialFieldSelected: (fieldId: string) => boolean
  setAnswer: (value: { fieldId: string; value: Answer['value'] }) => void
  canSubmit: ComputedRef<boolean>
}

export const useAnswerUtils: UseAnswerUtils = (answers, invalidAnswerIds, currentPromptType) => {
  const fieldIdToAnswerMap = computed(
    () => new Map(answers.value.map(answer => [answer.fieldId, answer])),
  )

  const getAnswerByFieldId: ReturnType<UseAnswerUtils>['getAnswerByFieldId'] = fieldId => {
    const answer = fieldIdToAnswerMap.value.get(fieldId)
    if (isUndefined(answer)) throw new Error(`cannot find answer with fieldId ${fieldId}`)
    return answer
  }

  const isSpecialFieldSelected: ReturnType<UseAnswerUtils>['isSpecialFieldSelected'] = id => {
    const answer = getAnswerByFieldId(id)
    assertAnswerType(answer.type, 'choice')
    return answer.value.selected
  }

  const setAnswer: ReturnType<UseAnswerUtils>['setAnswer'] = ({ fieldId, value }) => {
    if (currentPromptType.value?.type === 'question') {
      const targetAnswer = getAnswerByFieldId(fieldId)
      answers.value = answers.value.map(answer =>
        isSpecialFieldId(answer.fieldId)
          ? ({ ...answer, value: getDefaultAnswerValue(answer.type) } as Answer)
          : answer,
      )
      if (
        QUESTION_POLICIES[currentPromptType.value.question.type].shouldClearOnSpecialFieldUpdate &&
        isSpecialFieldId(targetAnswer.fieldId)
      ) {
        answers.value = answers.value.map(
          answer =>
            ({
              ...answer,
              value: getDefaultAnswerValue(answer.type),
            }) as Answer,
        )
      }
      answers.value = answers.value.map(a =>
        a.fieldId === targetAnswer.fieldId ? ({ ...a, value } as Answer) : a,
      )
      targetAnswer.value = value
      invalidAnswerIds.value = invalidAnswerIds.value.filter(id => id !== targetAnswer.fieldId)
    } else if (currentPromptType.value?.type === 'consent') {
      answers.value = [
        {
          id: 'consent',
          fieldId,
          type: 'consent',
          value: value as AnswerValue<'consent'>,
        },
      ]
    }
  }

  const canSubmit = computed<boolean>(() => {
    if (invalidAnswerIds.value.length > 0) return false
    if (!currentPromptType.value) return false
    if (currentPromptType.value.type === 'message') return true
    if (currentPromptType.value.type === 'roundLanding') return true
    if (currentPromptType.value.type === 'consent') return true
    if (currentPromptType.value.type === 'preVideoTestCard') return true
    if (currentPromptType.value.type === 'videoTest') return true
    if (currentPromptType.value.type === 'preVideoMessage') return true
    ;((_assertTypeIsQuestion: 'question') => undefined)(currentPromptType.value.type)
    if (currentPromptType.value.type !== 'question') return false
    return QUESTION_POLICIES[currentPromptType.value.question.type].canSubmit({
      question: currentPromptType.value.question,
      answers: answers.value,
    })
  })

  return {
    getAnswerByFieldId,
    isSpecialFieldSelected,
    setAnswer,
    canSubmit,
  }
}
